// Quick-add parser: turns "Montaggio reel domani alle 15 @Gianni !1 #RAG" into a structured task.
// Italian first. Only unambiguous tokens are recognized; everything else stays in the title.
import { addDays, diffDays, formatDate, parseDate, weekday } from './dates.ts';

export interface ParsePeople {
  id: string;
  displayName: string;
}
export interface ParseWorkspace {
  id: string;
  key: string;
  name: string;
}

export type TokenKind = 'date' | 'time' | 'assignee' | 'priority' | 'workspace';
export interface ParsedToken {
  kind: TokenKind;
  text: string;
  label: string;
}

export interface ParsedTask {
  title: string;
  dueDate: string | null;
  dueTime: string | null;
  priority: number | null;
  assigneeIds: string[];
  workspaceId: string | null;
  tokens: ParsedToken[];
}

const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const WEEKDAY_WORDS: Record<string, number> = {
  domenica: 0, lunedi: 1, martedi: 2, mercoledi: 3, giovedi: 4, venerdi: 5, sabato: 6,
};
const MONTH_WORDS: Record<string, number> = {
  gennaio: 1, gen: 1, febbraio: 2, feb: 2, marzo: 3, mar: 3, aprile: 4, apr: 4, maggio: 5, mag: 5, giugno: 6, giu: 6,
  luglio: 7, lug: 7, agosto: 8, ago: 8, settembre: 9, set: 9, ottobre: 10, ott: 10, novembre: 11, nov: 11, dicembre: 12, dic: 12,
};
const PRIORITY_LABELS = ['', 'Urgente', 'Alta', 'Media', 'Bassa'];

const pad = (n: number) => String(n).padStart(2, '0');

/** Next occurrence of a calendar day/month (this year, or next year if already past). */
function dayMonth(today: string, day: number, month: number, year?: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  let y = year ?? Number(today.slice(0, 4));
  if (y < 100) y += 2000;
  const candidate = `${y}-${pad(month)}-${pad(day)}`;
  if (formatDate(parseDate(candidate)) !== candidate) return null; // e.g. 31/02
  if (!year && diffDays(candidate, today) < 0) return `${y + 1}-${pad(month)}-${pad(day)}`;
  return candidate;
}

function time(h: number, m = 0): string | null {
  if (h > 23 || m > 59) return null;
  return `${pad(h)}:${pad(m)}`;
}

export function parseQuickAdd(
  input: string,
  ctx: { today: string; people?: ParsePeople[]; workspaces?: ParseWorkspace[] },
): ParsedTask {
  const result: ParsedTask = { title: '', dueDate: null, dueTime: null, priority: null, assigneeIds: [], workspaceId: null, tokens: [] };
  let text = ` ${input} `;

  const take = (re: RegExp, handler: (m: RegExpExecArray) => ParsedToken | null) => {
    const m = re.exec(text);
    if (!m) return;
    const token = handler(m);
    if (!token) return;
    result.tokens.push(token);
    text = text.slice(0, m.index) + ' ' + text.slice(m.index + m[0].length);
  };

  // --- time: "alle 15", "alle 15:30", "ore 9.30", "h15", "15:30"
  take(/\s(?:alle|ore|h)\s?(\d{1,2})(?:[:.](\d{2}))?(?=\s)/i, (m) => {
    const t = time(Number(m[1]), Number(m[2] ?? 0));
    if (!t) return null;
    result.dueTime = t;
    return { kind: 'time', text: m[0].trim(), label: t };
  });
  if (!result.dueTime) {
    take(/\s(\d{1,2}):(\d{2})(?=\s)/, (m) => {
      const t = time(Number(m[1]), Number(m[2]));
      if (!t) return null;
      result.dueTime = t;
      return { kind: 'time', text: m[0].trim(), label: t };
    });
  }

  // --- dates
  const setDate = (d: string, label: string, raw: string): ParsedToken => {
    result.dueDate = d;
    return { kind: 'date', text: raw, label };
  };
  take(/\s(oggi|domani|dopodomani)(?=\s)/i, (m) => {
    const w = fold(m[1]!);
    const n = w === 'oggi' ? 0 : w === 'domani' ? 1 : 2;
    return setDate(addDays(ctx.today, n), m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1).toLowerCase(), m[0].trim());
  });
  if (!result.dueDate) {
    take(/\s(?:tra|fra)\s(\d{1,3}|un|una)\s(giorn[oi]|settiman[ae])(?=\s)/i, (m) => {
      const n = /^\d/.test(m[1]!) ? Number(m[1]) : 1;
      const days = /^settiman/i.test(m[2]!) ? n * 7 : n;
      return setDate(addDays(ctx.today, days), m[0].trim(), m[0].trim());
    });
  }
  if (!result.dueDate) {
    take(/\s(?:la\s)?prossima\ssettimana(?=\s)/i, (m) => {
      const toMonday = ((8 - weekday(ctx.today)) % 7) || 7;
      return setDate(addDays(ctx.today, toMonday), 'Lunedì prossimo', m[0].trim());
    });
  }
  if (!result.dueDate) {
    take(/\s(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?(?=\s)/, (m) => {
      const d = dayMonth(ctx.today, Number(m[1]), Number(m[2]), m[3] ? Number(m[3]) : undefined);
      return d ? setDate(d, m[0].trim(), m[0].trim()) : null;
    });
  }
  if (!result.dueDate) {
    take(/\s(\d{1,2})\s([a-zàèéìòù]{3,9})(?:\s(\d{4}))?(?=\s)/i, (m) => {
      const month = MONTH_WORDS[fold(m[2]!)];
      if (!month) return null;
      const d = dayMonth(ctx.today, Number(m[1]), month, m[3] ? Number(m[3]) : undefined);
      return d ? setDate(d, m[0].trim(), m[0].trim()) : null;
    });
  }
  if (!result.dueDate) {
    take(/\s(?:(?:il|di|questo|questa)\s)?(luned[iì]|marted[iì]|mercoled[iì]|gioved[iì]|venerd[iì]|sabato|domenica)(?=\s)/i, (m) => {
      const target = WEEKDAY_WORDS[fold(m[1]!)]!;
      const delta = ((target - weekday(ctx.today) + 7) % 7) || 7; // same weekday → next week
      return setDate(addDays(ctx.today, delta), m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1).toLowerCase(), m[0].trim());
    });
  }
  // A time without a date means today
  if (result.dueTime && !result.dueDate) result.dueDate = ctx.today;

  // --- priority: "!1".."!4", "!!!" (urgente), "!!" (alta)
  take(/\s!([1-4])(?=\s)/, (m) => {
    result.priority = Number(m[1]);
    return { kind: 'priority', text: m[0].trim(), label: PRIORITY_LABELS[result.priority]! };
  });
  if (result.priority === null) {
    take(/\s(!!!?)(?=\s)/, (m) => {
      result.priority = m[1]!.length === 3 ? 1 : 2;
      return { kind: 'priority', text: m[0].trim(), label: PRIORITY_LABELS[result.priority]! };
    });
  }

  // --- workspace: "#RAG" or "#ragazze"
  if (ctx.workspaces?.length) {
    take(/\s#([\p{L}\p{N}_-]{2,40})(?=\s)/u, (m) => {
      const q = fold(m[1]!);
      const ws = ctx.workspaces!.find((w) => fold(w.key) === q) ?? ctx.workspaces!.find((w) => fold(w.name).startsWith(q));
      if (!ws) return null;
      result.workspaceId = ws.id;
      return { kind: 'workspace', text: m[0].trim(), label: ws.name };
    });
  }

  // --- assignees: "@Gianni", repeatable
  if (ctx.people?.length) {
    for (let i = 0; i < 5; i++) {
      const before = result.tokens.length;
      take(/\s@([\p{L}\p{N}._-]{2,40})(?=\s)/u, (m) => {
        const q = fold(m[1]!);
        const p =
          ctx.people!.find((x) => fold(x.displayName.split(' ')[0]!) === q) ??
          ctx.people!.find((x) => fold(x.displayName).replace(/\s+/g, '').startsWith(q));
        if (!p || result.assigneeIds.includes(p.id)) return null;
        result.assigneeIds.push(p.id);
        return { kind: 'assignee', text: m[0].trim(), label: p.displayName };
      });
      if (result.tokens.length === before) break;
    }
  }

  result.title = text.replace(/\s+/g, ' ').trim();
  return result;
}
